import { useEffect, useRef, useState } from "react";
import { apiClient } from "module/api";
import { authorization } from "module/auth";

export type CompanyUser = {
  /** Liveblocks-formatted id, e.g. "user-123". */
  id: string;
  /** Raw numeric id from the API. */
  rawId: number | string;
  name: string;
  email?: string;
  avatar?: string;
};

const DEBOUNCE_MS = 180;
const EMPTY_CACHE_TTL_MS = 5 * 60 * 1000;

// Cache the "empty query" (initial list) so opening the composer repeatedly
// doesn't re-hit the endpoint. Keyed by the current authenticated user id
// so a logout→login-as-different-user (without page refresh) does NOT serve
// the previous user's company list from cache — `getCacheOwner()` returns a
// different key, so the stale entry is ignored and refetched.
let emptyCache: {
  users: CompanyUser[];
  fetchedAt: number;
  ownerId: string | number | null;
} | null = null;

const getCacheOwner = (): string | number | null => {
  try {
    return authorization.getUser?.()?.id ?? null;
  } catch {
    return null;
  }
};

const isCacheFresh = (ownerId: string | number | null): boolean =>
  !!emptyCache && emptyCache.ownerId === ownerId && Date.now() - emptyCache.fetchedAt < EMPTY_CACHE_TTL_MS;

const mapResponse = (rows: any[]): CompanyUser[] =>
  (rows || [])
    .filter((u) => u?.id)
    .map((u) => {
      const fullName = [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email || `User ${u.id}`;
      return {
        id: `user-${u.id}`,
        rawId: u.id,
        name: fullName,
        email: u.email || undefined,
        avatar: u.profilePicture || undefined,
      };
    });

/**
 * Search the current company's users for @-mention autocomplete.
 *
 * Calls `GET /platform/users/mention-search?q=…` (minimal shape, server-scoped
 * to the JWT's client). Debounces by ~180ms; caches the empty-query result
 * for 5 minutes so the composer shows results immediately on open.
 */
export const useCompanyUsers = (query: string): { users: CompanyUser[]; isLoading: boolean } => {
  const q = (query || "").trim();
  const ownerId = getCacheOwner();
  const [users, setUsers] = useState<CompanyUser[]>(() => (!q && isCacheFresh(ownerId) ? emptyCache!.users : []));
  const [isLoading, setIsLoading] = useState<boolean>(() => !(!q && isCacheFresh(ownerId)));
  const reqIdRef = useRef(0);

  useEffect(() => {
    // Serve the empty-query cache synchronously if warm AND owned by the
    // current authenticated user (prevents cross-user leakage after a
    // logout→login without page refresh).
    if (!q && isCacheFresh(ownerId)) {
      setUsers(emptyCache!.users);
      setIsLoading(false);
      return;
    }

    const myReqId = ++reqIdRef.current;
    setIsLoading(true);
    const handle = window.setTimeout(() => {
      apiClient
        .searchMentionUsers({ q })
        .then((rows: any[]) => {
          if (myReqId !== reqIdRef.current) return; // a newer request superseded us
          const mapped = mapResponse(rows);
          if (!q) {
            emptyCache = { users: mapped, fetchedAt: Date.now(), ownerId };
          }
          setUsers(mapped);
          setIsLoading(false);
        })
        .catch(() => {
          if (myReqId !== reqIdRef.current) return;
          setUsers([]);
          setIsLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(handle);
    };
  }, [q]);

  return { users, isLoading };
};

/**
 * Explicit cache drop. Hook this into logout / user-switch side effects if a
 * future flow ever needs synchronous invalidation — today the `ownerId` key
 * already makes stale entries unreachable, but keeping this export avoids
 * having to reach into module internals later.
 */
export const clearCompanyUsersCache = (): void => {
  emptyCache = null;
};
